import { Box, Grid, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import StyledBackButton from '../../Common/StyledBackButton';
import StyledDeleteButton from '../../Common/StyledDeleteButton';
import StyledTypography from '../../Common/StyledTypography';
import LabeledInput from '../../Common/LabeledInput';
import { useAppSelector } from '../../../redux/hooks';
import { coachDeleteRequest } from '../../../redux/coach/coachSlice';
import { UserStatusType } from '../../../types/coach';

interface IDeleteActivityModalProps {
  handleDismiss: () => void;
  loading: boolean;
}

function DeleteActivityModal({ handleDismiss, loading }: IDeleteActivityModalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const coachId = useAppSelector((state) => state.coach.coach.id);
  const userStatusType = useAppSelector((state) => state.coach.coach?.userStatusType);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleDelete = () => {
    if (!coachId) return;

    if (userStatusType === UserStatusType.EXTERNAL_PROVIDER) {
      // If userStatusType is 'EXTERNAL_PROVIDER', dispatch delete request without password
      dispatch(coachDeleteRequest({ coachId }));
    } else {
      // If userStatusType is not 'EXTERNAL_PROVIDER', check for password and dispatch delete request with password
      if (!password) return;
      dispatch(coachDeleteRequest({ coachId, password }));
    }
  };

  return (
    <Grid container flexDirection="column">
      <Grid
        item
        p="36px"
        gap="11px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          background:
            'linear-gradient(175deg, rgba(255, 154, 154, 0.20) -9.81%, rgba(255, 88, 88, 0.20) 96.32%), #FFF',
        }}
        width="100%"
        borderRadius="10px 10px 0 0"
      >
        <img src="/assets/icons/trash-bin-large.svg" alt="bin" role="presentation" />
        <StyledTypography variant="title2Bold" textAlign="center">
          <span>{t('settings.appSettings.accountDeletion.title')}</span>
        </StyledTypography>
      </Grid>

      <Grid item p="52px 32px 36px 32px" gap={6} display="flex" flexDirection="column">
        <Typography variant="bodyRegular" color="primary" textAlign="center">
          {t('settings.appSettings.accountDeletion.description')}
        </Typography>
        {userStatusType !== UserStatusType.EXTERNAL_PROVIDER && (
          <Box width="100%">
            <LabeledInput
              label={t('auth.password')}
              required
              name="newPassword"
              placeholder={t('auth.password')}
              type={showPassword ? 'text' : 'password'}
              id="new-password"
              value={password}
              handleChange={handleChange}
              startIcon={<img src="/assets/icons/security.svg" alt="email" />}
              endIcon={
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handlePasswordVisibility}
                >
                  {showPassword ? (
                    <img src="/assets/icons/visibility.svg" alt="visibility" />
                  ) : (
                    <img src="/assets/icons/visibility-off.svg" alt="visibility-off" />
                  )}
                </IconButton>
              }
            />
          </Box>
        )}
        <Box display="flex" gap="20px">
          {!loading && (
            <StyledBackButton variant="outlined" fullWidth onClick={handleDismiss}>
              <span>{t('clientTable.deleteClient.dismiss')}</span>
            </StyledBackButton>
          )}
          <StyledDeleteButton
            onClick={handleDelete}
            loading={loading}
            loadingText={t('settings.appSettings.accountDeletion.loadingText')}
          >
            {t('common.confirm')}
          </StyledDeleteButton>
        </Box>
      </Grid>
    </Grid>
  );
}

export default DeleteActivityModal;
